import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/searchable-select";
import type { Player, ClubWithMembership } from "../../../shared/schema";
import { ChevronLeft, ChevronRight, Search, Star, X } from "lucide-react";

interface CreateAmericanoFormProps {
  playerId: string | null;
  onSuccess?: () => void;
}

const SCORE_MODE_VALUES = ["points_21", "points_13", "points_24", "points_32", "classic_sets", "timed"] as const;

export default function CreateAmericanoForm({ playerId, onSuccess }: CreateAmericanoFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 state
  const [name, setName] = useState("");
  const [clubId, setClubId] = useState<string | null>(null);
  const handleClubChange = (val: string) => {
    setClubId(val || null);
    setLeftPlayerIds([]);
    setRightPlayerIds([]);
  };
  const [scoreMode, setScoreMode] = useState("points_21");
  const [customPoints, setCustomPoints] = useState<number>(15);
  const [numberOfCourts, setNumberOfCourts] = useState(1);

  // Step 2 state
  const [leftPlayerIds, setLeftPlayerIds] = useState<string[]>([]);
  const [rightPlayerIds, setRightPlayerIds] = useState<string[]>([]);
  const [playerSearch, setPlayerSearch] = useState("");

  const followKey = playerId ? `followedPlayers_${playerId}` : "followedPlayers";
  const followedPlayers = new Set<string>(JSON.parse(localStorage.getItem(followKey) || "[]"));

  const { data: allPlayers = [] } = useQuery<Player[]>({ queryKey: ["/api/players"] });
  const { data: clubMembers } = useQuery<Player[]>({
    queryKey: ["/api/clubs", clubId, "members"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clubs/${clubId}/members`);
      return res.json();
    },
    enabled: !!clubId,
  });
  const players = (clubId && clubMembers ? clubMembers : allPlayers).slice().sort((a, b) => {
    const aFollowed = followedPlayers.has(a.id);
    const bFollowed = followedPlayers.has(b.id);
    if (aFollowed && !bFollowed) return -1;
    if (!aFollowed && bFollowed) return 1;
    return a.name.localeCompare(b.name);
  });
  const { data: myClubs = [] } = useQuery<ClubWithMembership[]>({
    queryKey: ["/api/clubs", playerId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clubs?playerId=${playerId}`);
      return res.json();
    },
    enabled: !!playerId,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/americano", {
        name,
        clubId: clubId || null,
        scoreMode,
        timeLimitMinutes: scoreMode === "timed" ? customPoints : null,
        numberOfCourts,
        leftPlayerIds,
        rightPlayerIds,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erreur");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/americano"] });
      queryClient.invalidateQueries({ queryKey: ["/api/americano/active"] });
      toast({ title: t("americano.create.successToast") });
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    },
  });

  const clubOptions = myClubs
    .filter((c) => c.isMember && c.status !== "pending")
    .map((c) => ({ value: c.id, label: c.name }));

  const canProceedStep1 = name.trim().length > 0;
  const canProceedStep2 =
    leftPlayerIds.length >= 2 &&
    rightPlayerIds.length >= 2 &&
    leftPlayerIds.length === rightPlayerIds.length;

  const totalPlayers = leftPlayerIds.length + rightPlayerIds.length;

  // Generate preview schedule info
  const nLeft = leftPlayerIds.length;
  const estimatedRounds = nLeft > 0 ? nLeft * (nLeft - 1) : 0; // rough upper bound

  return (
    <div className="space-y-4">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-2">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`h-2 rounded-full transition-all ${
              s === step ? "w-8 bg-padel-green" : s < step ? "w-4 bg-padel-green/60" : "w-4 bg-gray-200 dark:bg-gray-700"
            }`}
          />
        ))}
      </div>

      {/* ── Step 1: Configuration ── */}
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t("americano.create.stepConfig")}</h3>

          <div>
            <label className="text-sm font-medium mb-1 block">{t("americano.create.nameLabel")}</label>
            <input
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-border rounded-lg bg-transparent focus:ring-2 focus:ring-padel-green focus:border-transparent"
              placeholder={t("americano.create.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">{t("americano.create.clubLabel")}</label>
            <SearchableSelect
              value={clubId}
              onChange={handleClubChange}
              options={[{ value: "", label: t("americano.create.noClub") }, ...clubOptions]}
              placeholder={t("americano.create.clubPlaceholder")}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">{t("americano.create.scoreModeLabel")}</label>
            <div className="grid grid-cols-2 gap-2">
              {SCORE_MODE_VALUES.map((value) => (
                <button
                  key={value}
                  onClick={() => setScoreMode(value)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    scoreMode === value
                      ? "border-[#001489] bg-[#001489] text-white font-medium"
                      : "border-gray-200 dark:border-border hover:border-gray-300 dark:text-foreground"
                  }`}
                >
                  {t(`americano.scoreMode.${value}`)}
                </button>
              ))}
            </div>
          </div>

          {scoreMode === "timed" && (
            <div>
              <label className="text-sm font-medium mb-1 block">{t("americano.create.timeLimitLabel")}</label>
              <select
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-border rounded-lg bg-transparent dark:bg-card focus:ring-2 focus:ring-padel-green"
                value={customPoints}
                onChange={(e) => setCustomPoints(Number(e.target.value))}
              >
                {[10, 12, 15, 16, 18, 20, 25, 30, 40, 50].map((n) => (
                  <option key={n} value={n}>{n} pts</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium mb-1 block">{t("americano.create.courtsLabel")}</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <button
                  key={n}
                  onClick={() => setNumberOfCourts(n)}
                  className={`w-9 h-9 text-sm rounded-lg border transition-colors ${
                    numberOfCourts === n
                      ? "border-[#001489] bg-[#001489] text-white font-medium"
                      : "border-gray-200 dark:border-border hover:border-gray-300 dark:text-foreground"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <Button
            className="w-full bg-padel-green hover:bg-padel-dark-green text-white"
            disabled={!canProceedStep1}
            onClick={() => setStep(2)}
          >
            {t("americano.create.next")} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* ── Step 2: Players ── */}
      {step === 2 && (() => {
        const togglePlayer = (id: string) => {
          if (leftPlayerIds.includes(id)) {
            setLeftPlayerIds((ids) => ids.filter((i) => i !== id));
            setRightPlayerIds((ids) => [...ids, id]);
          } else if (rightPlayerIds.includes(id)) {
            setRightPlayerIds((ids) => ids.filter((i) => i !== id));
          } else {
            setLeftPlayerIds((ids) => [...ids, id]);
          }
        };

        const filteredPlayers = players.filter((p) =>
          p.name.toLowerCase().includes(playerSearch.toLowerCase())
        );

        return (
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              {t("americano.create.stepPlayers")}
            </h3>

            {/* Score header */}
            <div className="flex items-center justify-between rounded-xl border px-4 py-2.5 bg-gray-50 dark:bg-muted">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-bold">G</span>
                <span className="text-sm font-semibold">{leftPlayerIds.length}</span>
              </div>
              <p className="text-xs text-muted-foreground text-center px-2">
                {t("americano.create.playersInstructionGrid")}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{rightPlayerIds.length}</span>
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs font-bold">D</span>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-border rounded-lg bg-transparent focus:ring-2 focus:ring-padel-green focus:border-transparent"
                placeholder={t("americano.create.searchPlayers")}
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
              />
              {playerSearch && (
                <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setPlayerSearch("")}>
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Player grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[52vh] overflow-y-auto pr-0.5">
              {filteredPlayers.map((p) => {
                const isLeft = leftPlayerIds.includes(p.id);
                const isRight = rightPlayerIds.includes(p.id);
                const isFollowed = followedPlayers.has(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => togglePlayer(p.id)}
                    className={`relative flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-all active:scale-95 ${
                      isLeft
                        ? "border-blue-400 bg-blue-50 dark:bg-blue-950/40 dark:border-blue-600 text-blue-800 dark:text-blue-200"
                        : isRight
                        ? "border-green-400 bg-green-50 dark:bg-green-950/40 dark:border-green-600 text-green-800 dark:text-green-200"
                        : "border-gray-200 dark:border-border hover:border-gray-300 dark:text-foreground"
                    }`}
                  >
                    {isLeft || isRight ? (
                      <span className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${
                        isLeft
                          ? "bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-200"
                          : "bg-green-200 dark:bg-green-800 text-green-700 dark:text-green-200"
                      }`}>
                        {isLeft ? "G" : "D"}
                      </span>
                    ) : isFollowed ? (
                      <Star className="shrink-0 h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                    ) : null}
                    <span className="truncate">{p.name}</span>
                  </button>
                );
              })}
            </div>

            {leftPlayerIds.length !== rightPlayerIds.length && leftPlayerIds.length > 0 && rightPlayerIds.length > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {t("americano.create.unequalWarning")}
              </p>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>
                <ChevronLeft className="h-4 w-4 mr-1" /> {t("americano.create.back")}
              </Button>
              <Button
                className="flex-1 bg-padel-green hover:bg-padel-dark-green text-white"
                disabled={!canProceedStep2}
                onClick={() => setStep(3)}
              >
                {t("americano.create.next")} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        );
      })()}

      {/* ── Step 3: Confirmation ── */}
      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t("americano.create.stepConfirm")}</h3>

          <div className="bg-gray-50 dark:bg-muted rounded-xl p-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("americano.create.confirmName")}</span>
              <span className="font-medium">{name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("americano.create.confirmScore")}</span>
              <span className="font-medium">
                {t(`americano.scoreMode.${scoreMode}`)}
              </span>
            </div>
            {scoreMode === "timed" && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("americano.create.confirmDuration")}</span>
                <span className="font-medium">{customPoints} pts</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("americano.create.confirmCourts")}</span>
              <span className="font-medium">{numberOfCourts}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("americano.create.confirmPlayers")}</span>
              <span className="font-medium">{totalPlayers} ({leftPlayerIds.length}G + {rightPlayerIds.length}D)</span>
            </div>
            {clubId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("americano.create.confirmClub")}</span>
                <span className="font-medium">{myClubs.find((c) => c.id === clubId)?.name}</span>
              </div>
            )}
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-3 text-xs text-blue-700 dark:text-blue-300">
            {t("americano.create.scheduleInfo")}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setStep(2)}>
              <ChevronLeft className="h-4 w-4 mr-1" /> {t("americano.create.back")}
            </Button>
            <Button
              className="flex-1 bg-padel-green hover:bg-padel-dark-green text-white"
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? t("americano.create.creating") : t("americano.create.create")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
